import { useQuery, useMutation } from '@tanstack/react-query';
import { QueryKeys } from '@enums/queryKeys';
import { httpService } from '@services/httpService';
import { ApiRoute } from '@routes/apiRoutes';
import { ProfileRequestModel } from '@models/profile/profileRequestModel';
import { AccountModel } from '@models/profile/accountModel';
import { MatchingEntityModel, GetMatchingAccountsRequest } from '@models/profile/matchingEntityModel';
import { VerifyLinkAccountRequest, VerifyLinkAccountResponse } from '@models/profile/verifyLinkAccountRequestModel';

export const useFetchAccountsApi = () => {
  const portfolio = async () => {
    return await httpService.get<AccountModel[]>(ApiRoute.Accounts);
  };
  return useQuery({
    queryKey: [QueryKeys.ACCOUNTS],
    queryFn: () => portfolio(),
  });
};

export const useUnlinkAccountApi = () => {
  const unlinkAccount = async (lenderRecId: string) => {
    return await httpService.delete(ApiRoute.UnlinkAccount, lenderRecId);
  };

  return useMutation({
    mutationFn: (lenderRecId: string) => unlinkAccount(lenderRecId),
  });
};

export const usePostLinkAccountApi = () => {
  const linkAccount = async (request: ProfileRequestModel) => {
    return await httpService.post(ApiRoute.LinkAccount, request);
  };

  return useMutation({
    mutationFn: (request: ProfileRequestModel) => linkAccount(request),
  });
};

export const useUpdatePasswordApi = () => {
  const updatePassword = async (request: ProfileRequestModel) => {
    return await httpService.post(ApiRoute.UpdatePassword, request);
  };

  return useMutation({
    mutationFn: (request: ProfileRequestModel) => updatePassword(request),
  });
};

export interface AnnouncementStatusResponse {
  hasHiddenNewPortalAnnouncement: boolean;
}

export const useFetchAnnouncementStatusApi = (enabled = true) => {
  const fetchStatus = async () => {
    return await httpService.get<AnnouncementStatusResponse>(
      ApiRoute.GetAnnouncementStatus
    );
  };

  return useQuery({
    queryKey: [QueryKeys.ANNOUNCEMENT_STATUS],
    queryFn: () => fetchStatus(),
    enabled,
  });
};

export interface SetAnnouncementHiddenResponse {
  Success: boolean;
}

export const useSetAnnouncementHiddenApi = () => {
  const setAnnouncementHidden = async () => {
    return await httpService.post<SetAnnouncementHiddenResponse>(
      ApiRoute.SetAnnouncementHidden
    );
  };

  return useMutation({
    mutationFn: () => setAnnouncementHidden(),
  });
};
export const useGetMatchingAccountsApi = () => {
  const getMatchingAccounts = async (request: GetMatchingAccountsRequest) => {
    return await httpService.post<MatchingEntityModel[]>(ApiRoute.GetMatchingAccounts, request);
  };

  return useMutation({
    mutationFn: (request: GetMatchingAccountsRequest) => getMatchingAccounts(request),
  });
};

export const useVerifyAndLinkAccountApi = () => {
  const verifyAndLinkAccount = async (request: VerifyLinkAccountRequest) => {
    return await httpService.post<VerifyLinkAccountResponse>(ApiRoute.VerifyAndLinkAccount, request);
  };

  return useMutation({
    mutationFn: (request: VerifyLinkAccountRequest) => verifyAndLinkAccount(request),
  });
};
